import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { config } from "@noted/configuration";
import { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { Context, createSession, isDarkNotedUser, useGetContextsQuery } from "../api/context-api";

async function navigateToApp(context: Context, session?: string | Promise<string>) {
  const url = isDarkNotedUser(context) ? config.darkNotedUrl : config.appUrl;
  const s = session ? await session : context.session;
  window.location.href = url + "?s=" + s;
}

function hasValidSingleContext(contexts: Context[]) {
  return contexts.length === 1 && !contexts[0].isSuspended;
}

function ContextSelector() {
  const { getAccessToken } = useKindeAuth();
  const { data } = useGetContextsQuery();

  const createSessionRunning = useRef<boolean>(false);

  useEffect(() => {
    // This is a little tricky!!
    // It is possible that the component is mounted, unmounted and then mounted again.
    // This happens in development when in React.Strict mode but could also happen in production,
    // therefore it is important that our component correctly handles this.
    //
    // We are doing this by tracking if the API call to create the session has been called already,
    // and while this works I'm not sure if this is the best.  Better options seem to be to do all
    // this work server side, but as we are SPA this is not possible.  Another option is to change
    // the API to support multiply calls to the "create session" and this might be needed anyway as
    // I can see the possibility of the user being able to do this by simply opening multiply tabs,
    // so we will need to keep and eye on this.
    //
    // Also, using react-query to create the session is problematic because every time we mount we
    // run the hook, and while that doesn't change the mutate function it does change the observers
    // on the mutation, and things don't seem to work as expected.  Therefore, we are using a fetch
    // call directly.

    // if we have a "create session" running stop ...
    if (createSessionRunning.current) {
      return;
    }

    // if we have a single context, lets create a session for it and navigate to that app ...
    if (hasValidSingleContext(data)) {
      createSessionRunning.current = true;

      // if the context has a session we can just go there ...
      if (data[0].session) {
        void navigateToApp(data[0]);
        return;
      }

      // we have no session, so we need to create one ...
      void navigateToApp(data[0], createSessionAndNavigate());
    }

    async function createSessionAndNavigate() {
      const token = await getAccessToken();
      return await createSession(token, data[0].id);
    }
  }, [createSession, createSessionRunning, data, getAccessToken, navigateToApp]);

  // a single context is handled by the useEffect, so no need for a UI
  if (hasValidSingleContext(data)) return null;

  return <Navigate to="users" replace={true} />;
}

export default ContextSelector;
